/**
 * REGISTRE DES ÉTAPES DU SUIVI DE PRESTATION (Prestataire).
 *
 * Même principe que pipelineStages.ts côté Suivi privatisation (Restauration) — un seul endroit pour les
 * libellés et l'ordre — mais code entièrement séparé : rien n'est partagé entre les deux systèmes.
 *
 * Le dossier naît d'un coup de téléphone (nom, email, téléphone, particulier ou professionnel), le devis
 * est composé et généré DANS l'appli (pas un fichier téléversé), signé par un dépôt manuel du client via un
 * lien public, puis le pipeline suit le rendez-vous facturé jusqu'à sa clôture — sans jamais recopier ce
 * rendez-vous ni sa facture dans le dossier.
 */

export const PIPELINE_STAGE_ORDER_PRESTATION = [
    // Une demande arrivée seule, par le simulateur du site (2026-09-20) : personne de l'établissement ne
    // l'a encore lue. Elle attend d'être étudiée (photos, sélection du visiteur, estimation indicative)
    // avant que le devis ne soit composé — d'où une étape à elle, devant « Dossier créé », qui reste
    // l'étape de naissance d'un dossier ouvert à la main après un appel.
    'demande_recue',
    'dossier_cree',
    'devis_envoye',
    'devis_signe',
    'rdv_reserve',
    'prestation_realisee',
    'cloturee',
] as const;

export const PIPELINE_STAGE_TERMINAL_PRESTATION = ['perdue', 'annulee'] as const;

export type PipelineStagePrestation =
    | (typeof PIPELINE_STAGE_ORDER_PRESTATION)[number]
    | (typeof PIPELINE_STAGE_TERMINAL_PRESTATION)[number];

export const PIPELINE_STAGE_LABELS_PRESTATION: Record<PipelineStagePrestation, string> = {
    demande_recue: 'Demande reçue',
    dossier_cree: 'Dossier créé',
    devis_envoye: 'Devis envoyé',
    devis_signe: 'Devis signé',
    rdv_reserve: 'Rendez-vous réservé',
    prestation_realisee: 'Prestation réalisée',
    cloturee: 'Clôturé',
    perdue: 'Abandonné',
    annulee: 'Annulé',
};

export const PIPELINE_STAGE_PENDING_LABELS_PRESTATION: Record<PipelineStagePrestation, string> = {
    demande_recue: 'Demande à étudier',
    dossier_cree: 'Devis à établir',
    devis_envoye: 'En attente de signature',
    devis_signe: 'Rendez-vous à réserver',
    rdv_reserve: 'Prestation à réaliser',
    prestation_realisee: 'À clôturer',
    cloturee: 'Clôturé',
    perdue: 'Abandonné',
    annulee: 'Annulé',
};

export const PIPELINE_STAGE_PIVOT_PRESTATION: PipelineStagePrestation = 'devis_signe';

export const isTerminalStagePrestation = (stage?: string | null): boolean =>
    !!stage && (PIPELINE_STAGE_TERMINAL_PRESTATION as readonly string[]).includes(stage);

export const isKnownStagePrestation = (stage?: string | null): stage is PipelineStagePrestation =>
    !!stage && stage in PIPELINE_STAGE_LABELS_PRESTATION;

export const stageRankPrestation = (stage?: string | null): number =>
    (PIPELINE_STAGE_ORDER_PRESTATION as readonly string[]).indexOf(String(stage || ''));

export const hasPassedPivotPrestation = (stage?: string | null): boolean =>
    stageRankPrestation(stage) >= stageRankPrestation(PIPELINE_STAGE_PIVOT_PRESTATION);

// Demande d'Emeni (2026-09-04) : quand le devis signé porte une formule, un contrat d'abonnement doit être
// envoyé puis signé AVANT de pouvoir réserver le rendez-vous d'intervention — ce qui se passait déjà
// (« attendContrat » bloque la réservation tant que l'abonnement n'est pas actif), mais restait invisible
// dans la frise : tout restait affiché sur « Devis signé » pendant tout ce temps. Ces deux étapes n'existent
// QUE pour l'affichage (jamais stockées comme `dossier.stage` réel, qui reste dossier_cree → devis_envoye →
// devis_signe → rdv_reserve → ... sans changement) — dérivées du statut de l'abonnement (subscriptions.status)
// à l'instant où on lit le dossier. Sans formule (particulier, ou pro sans engagement), ces deux étapes
// n'apparaissent jamais : la frise reste identique à avant.
export const CONTRACT_DISPLAY_STAGE_LABELS: Record<'contrat_envoye' | 'contrat_signe', string> = {
    contrat_envoye: 'Contrat envoyé',
    contrat_signe: 'Contrat signé',
};

export type DisplayStageKeyPrestation = PipelineStagePrestation | 'contrat_envoye' | 'contrat_signe';

export interface DisplayStagePrestation {
    key: DisplayStageKeyPrestation;
    label: string;
    done: boolean;
    current: boolean;
}

/**
 * Construit la frise à afficher pour un dossier donné — insère « Contrat envoyé » / « Contrat signé » entre
 * « Devis signé » et « Rendez-vous réservé » uniquement si le devis porte une formule (abonnement).
 *
 * @param dossier objet renvoyé par GET /prestation-dossiers/:id — utilise dossier.stage,
 *   dossier.devis?.formula_subscription_id et dossier.subscription?.status.
 */
export function buildDisplayStagesPrestation(dossier: {
    stage?: string | null;
    source?: string | null;
    devis?: { formula_subscription_id?: string | null } | null;
    subscription?: { status?: string | null } | null;
}): DisplayStagePrestation[] {
    const hasFormula = !!dossier?.devis?.formula_subscription_id;
    const keysCompletes: DisplayStageKeyPrestation[] = hasFormula
        ? ['demande_recue', 'dossier_cree', 'devis_envoye', 'devis_signe', 'contrat_envoye', 'contrat_signe', 'rdv_reserve', 'prestation_realisee', 'cloturee']
        : (PIPELINE_STAGE_ORDER_PRESTATION as unknown as DisplayStageKeyPrestation[]);

    // « Demande reçue » n'existe que pour les dossiers nés seuls, d'une demande de devis faite sur le site.
    // Un dossier ouvert à la main après un appel n'est jamais passé par là : afficher l'étape comme
    // franchie lui ferait raconter une histoire fausse, donc elle est retirée de sa frise.
    const venueDuSite = dossier?.source === 'simulateur' || dossier?.stage === 'demande_recue';
    const keys = venueDuSite ? keysCompletes : keysCompletes.filter((k) => k !== 'demande_recue');

    const stage = dossier?.stage;
    let currentIndex: number;
    if (stage !== 'devis_signe' || !hasFormula) {
        // En dehors de « Devis signé » (ou sans formule), la machine réelle fait autorité — l'étape
        // courante est simplement la position de dossier.stage dans la liste affichée.
        currentIndex = keys.indexOf((stage as DisplayStageKeyPrestation) || keys[0]);
        if (currentIndex < 0) currentIndex = 0;
    } else {
        // Toujours « Devis signé » côté machine réelle : la sous-position virtuelle vient du statut de
        // l'abonnement — jamais stockée, recalculée à chaque lecture.
        const subStatus = dossier?.subscription?.status;
        if (!dossier?.subscription) currentIndex = keys.indexOf('contrat_envoye');
        else if (subStatus === 'actif') currentIndex = keys.indexOf('rdv_reserve');
        else if (subStatus === 'envoye' || subStatus === 'consulte') currentIndex = keys.indexOf('contrat_signe');
        else currentIndex = keys.indexOf('contrat_envoye'); // brouillon, refusé, résilié : à renvoyer
    }

    return keys.map((key, i) => ({
        key,
        label: key === 'contrat_envoye' || key === 'contrat_signe'
            ? CONTRACT_DISPLAY_STAGE_LABELS[key]
            : PIPELINE_STAGE_LABELS_PRESTATION[key as PipelineStagePrestation],
        done: i < currentIndex,
        current: i === currentIndex,
    }));
}
